import { LIMITS } from '../data/limits';
import { findRoleTrack } from '../data/roleKeywords';
import { canonicalSkill, cleanList, findPhrases, gradient } from '../text';
import type { Rule, RuleContext } from '../types';
import { defineRule, findingsFor, flag, pass, skip } from './helpers';

/**
 * Skills rules.
 *
 * Skills are the most mechanical part of a LinkedIn profile and the easiest to
 * fix, which is exactly why they are worth checking: recruiters filter candidate
 * searches by skill, so a missing skill is not a weak signal — it is a filter
 * you fail outright.
 *
 * The first `LIMITS.skills.pinned` skills matter disproportionately because they
 * are the only ones shown without the reader expanding the section, so ordering
 * gets its own rule.
 */

const f = findingsFor('skills');

const find = (ctx: RuleContext) => cleanList(ctx.profile.skills);

export const skillsRules: readonly Rule[] = [
  defineRule({
    id: 'skills.too-few',
    section: 'skills',
    weight: 3,
    evaluate(ctx) {
      const skills = find(ctx);

      if (skills.length === 0) {
        return flag(
          0,
          f(
            'none',
            'critical',
            'You have not listed any skills',
            'Recruiters filter searches by skill, so a profile with none is excluded from those results before anyone reads a word of it. Skills also unlock endorsements and LinkedIn skill assessments.',
            `Add at least ${LIMITS.skills.minRecommended} to start, working toward ${LIMITS.skills.goodCount}. Pull them straight from your own project and role descriptions.`
          )
        );
      }

      if (skills.length >= LIMITS.skills.minRecommended) return pass();

      return flag(
        gradient(skills.length, 0, LIMITS.skills.minRecommended),
        f(
          'too-few',
          'critical',
          `Only ${skills.length} skill${skills.length === 1 ? '' : 's'} listed`,
          `Below about ${LIMITS.skills.minRecommended} skills, skill-based search barely surfaces your profile, and LinkedIn treats the section as incomplete.`,
          `Add skills until you have at least ${LIMITS.skills.minRecommended}. Include tools, languages, frameworks, and methods — anything you could answer a question about.`
        )
      );
    },
  }),

  defineRule({
    id: 'skills.thin',
    section: 'skills',
    weight: 2,
    evaluate(ctx) {
      const skills = find(ctx);
      // `skills.too-few` already owns the low end.
      if (skills.length < LIMITS.skills.minRecommended) return skip();
      if (skills.length >= LIMITS.skills.goodCount) return pass();

      return flag(
        gradient(skills.length, LIMITS.skills.minRecommended, LIMITS.skills.goodCount),
        f(
          'thin',
          'polish',
          `${skills.length} skills listed — aim for around ${LIMITS.skills.goodCount}`,
          `Every skill is a search you can appear in, and you have ${LIMITS.skills.hard} slots available. There is no downside to filling more of them as long as each one is true.`,
          `Work up to about ${LIMITS.skills.goodCount}. Mix hard skills (tools, languages) with the few soft skills you can actually evidence from your experience.`
        )
      );
    },
  }),

  defineRule({
    id: 'skills.over-limit',
    section: 'skills',
    weight: 1,
    evaluate(ctx) {
      const skills = find(ctx);
      if (skills.length === 0) return skip();
      if (skills.length <= LIMITS.skills.hard) return pass();

      return flag(
        0.3,
        f(
          'over-limit',
          'important',
          `${skills.length} skills — LinkedIn allows ${LIMITS.skills.hard}`,
          `You cannot save more than ${LIMITS.skills.hard} skills, so the extras will be rejected — and a list that long dilutes the signal anyway.`,
          `Cut to the ${LIMITS.skills.hard} that are most relevant to the roles you want, and make sure the strongest are in the first ${LIMITS.skills.pinned}.`
        )
      );
    },
  }),

  defineRule({
    id: 'skills.duplicates',
    section: 'skills',
    weight: 1,
    evaluate(ctx) {
      const skills = find(ctx);
      if (skills.length === 0) return skip();

      // `canonicalSkill` strips case, spacing, and punctuation, so 'Node.js',
      // 'nodejs', and 'node JS' all collapse to the same key.
      const seen = new Map<string, string>();
      const duplicates: string[] = [];

      for (const skill of skills) {
        const key = canonicalSkill(skill);
        if (!key) continue;
        const first = seen.get(key);
        if (first) {
          duplicates.push(`"${skill}" duplicates "${first}"`);
        } else {
          seen.set(key, skill);
        }
      }

      if (duplicates.length === 0) return pass();

      return flag(
        gradient(duplicates.length, 5, 0),
        f(
          'duplicates',
          'polish',
          `${duplicates.length} skill${duplicates.length === 1 ? ' is a' : 's are'} duplicate${duplicates.length === 1 ? '' : 's'}`,
          'Listing the same skill twice under different spellings wastes a slot and splits any endorsements between the two entries instead of concentrating them.',
          'Keep one spelling of each — preferably the one LinkedIn suggests as you type, since that is the version its search matches against.',
          duplicates.slice(0, 5)
        )
      );
    },
  }),

  defineRule({
    id: 'skills.top-alignment',
    section: 'skills',
    weight: 2,
    evaluate(ctx) {
      const skills = find(ctx);
      if (skills.length === 0) return skip();

      const track = findRoleTrack(ctx.profile.targetRole);
      // Without a target role there is nothing to align to, so this rule drops
      // out of scoring rather than counting as a failure.
      if (!track) return skip();

      const pinned = skills.slice(0, LIMITS.skills.pinned);
      const relevant = [...track.must, ...track.nice];
      const aligned = pinned.filter((skill) => findPhrases(skill, relevant).length > 0);

      if (aligned.length > 0) return pass();

      return flag(
        0.35,
        f(
          'top-alignment',
          'important',
          `None of your top ${LIMITS.skills.pinned} skills relates to ${track.label}`,
          `Only the first ${LIMITS.skills.pinned} skills show on your profile without the reader expanding the section, and LinkedIn treats them as your headline skills. Right now they point somewhere other than the role you want.`,
          `Reorder so your ${LIMITS.skills.pinned} most role-relevant skills come first. On LinkedIn this is the "Reorder" option in the Skills section.`,
          pinned
        )
      );
    },
  }),

  defineRule({
    id: 'skills.role-gaps',
    section: 'skills',
    weight: 2,
    evaluate(ctx) {
      const skills = find(ctx);
      const track = findRoleTrack(ctx.profile.targetRole);
      if (!track || skills.length === 0) return skip();

      // Deliberately narrower than `about.keyword-coverage`: LinkedIn's skill
      // filter searches the skills list specifically, so a term that appears
      // only in your About does not make you findable through it.
      const listed = skills.join('\n');
      const present = findPhrases(listed, track.must);
      const missing = track.must.filter((keyword) => !present.includes(keyword));
      const coverage = present.length / track.must.length;

      if (coverage >= LIMITS.keywords.coverageTarget) return pass();

      return flag(
        gradient(coverage, 0, LIMITS.keywords.coverageTarget),
        f(
          'role-gaps',
          'important',
          `Your skills list is missing common ${track.label} terms`,
          'Recruiters narrow candidate searches with the skill filter, which reads this list and nothing else. A term mentioned in your About but absent here will not get you through that filter.',
          'Add the ones that are genuinely true of you — and only those. An unearned skill on your list is a question you cannot answer in the interview.',
          missing.slice(0, 8)
        )
      );
    },
  }),
];
